import React, { useState } from "react";
import { View, Text, TextInput, StyleSheet, Platform, SafeAreaView } from "react-native";
import { useTranslation } from "../../../../i18n/useTranslation";
import { useSessionStore } from "../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../state/src/useSessionStore";
import Colors from "../../../../constants/colors";
import { inputAssistDisabledProps } from "../FloatingTextInputComponent";
import { ComponentStyles } from "../../../../constants/componentStyles";

type Props = {
  title?: string;
  subTitle?: string;
  pinError?: string;
  apiError?: string;
  pin: string[];
  handleChange: (text: string, index: number) => void;
  inputsRef: React.MutableRefObject<(TextInput | null)[]>;
  attemptsLeft?: number;
  locked?: boolean;
  showAttemptsWarning?: boolean;
};

const PinInputComponent: React.FC<Props> = ({
  title,
  subTitle,
  pinError,
  apiError,
  pin,
  handleChange,
  inputsRef,
  attemptsLeft,
  locked,
  showAttemptsWarning = true
}) => {
  const { t } = useTranslation();
  const [focusedIndex, setFocusedIndex] = useState<number>(0);
  const isNewConsumer = useSessionStore((s: SessionState) => (s as any).isNewConsumer);
  const Wrapper = Platform.OS === "web" ? View : SafeAreaView;
  const hasValidationError = !isNewConsumer && !locked && !!apiError;
  const suppressWebFocusRing = (e: any) => {
    if (Platform.OS !== "web") return;
    const el = e?.target as any;
    if (!el?.style) return;
    el.style.outline = "none";
    el.style.boxShadow = "none";
    el.style.webkitTapHighlightColor = "transparent";
  };

  const accountLockedBorder = (locked || hasValidationError) ? Colors.errorRedColor : Colors.black;
  console.log('accountLockedBorder=>', accountLockedBorder, locked)
  return (
    <Wrapper style={styles.container}>
      <Text style={styles.title}>{title}</Text>
      <Text style={styles.subtitle}>{subTitle}</Text>

      <View style={styles.pinContainer}>
        {pin.map((digit, index) => (
          <View
            style={[
              styles.pinBox,
              {
                borderColor: accountLockedBorder,
                borderWidth: (locked || hasValidationError)
                  ? 1
                  : (!isNewConsumer && focusedIndex === index ? 1 : 0),
              },
            ]}
          >
            <TextInput
              key={index}
              ref={(el) => (inputsRef.current[index] = el)}
              autoFocus={false}
              style={[styles.pinInput, { borderColor: accountLockedBorder, borderWidth: locked ? 1 : (hasValidationError ? 1 : (!isNewConsumer && focusedIndex === index ? 1 : 0))}]}
              keyboardType="number-pad"
              maxLength={1}
              secureTextEntry
              value={digit}
              onChangeText={(text) => handleChange(text, index)}
              onFocus={(e: any) => {
                suppressWebFocusRing(e);
                setFocusedIndex(index);
              }}
              {...inputAssistDisabledProps}
            />
          </View>
        ))}
      </View>

      {isNewConsumer ? null : locked ? (
          <Text style={styles.locked}>{t("ACCOUNT_LOCKED")}</Text>
        ) : showAttemptsWarning ? (
        <Text style={[styles.attempts, hasValidationError && styles.attemptsError]}>
          {attemptsLeft} {attemptsLeft === 1 ? "attempt" : "attempts"} remaining
        </Text>
        ) : !locked ? (
        <View />
      ) : null}
    </Wrapper>
  );
}

export default PinInputComponent;

const styles = StyleSheet.create({
  container: {
    justifyContent: "flex-start",
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 32
  },
  pinContainer: {
    flexDirection: "row",
    marginBottom: 8,
    justifyContent: 'space-between',
  },
  pinInput: {
    borderRadius: 8,
    flex: 1,
    textAlign: "center",
    fontSize: 22,
    fontWeight: "600",
    backgroundColor: Colors.searchCompanyBackground,
    ...(Platform.OS === 'web' && {
      outlineWidth: 0,
      outline: 'none',
      border: 'none',
      boxShadow: 'none',
    } as any),
  },
  pinBox: {
    width: 56,
    height: 56,
    borderRadius: 8,
    textAlign: "center",
  },
  error: {
    color: "red",
    fontSize: 12,
    marginTop: 8,
  },
  attempts: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 23,
    color: "#6B7280",
    marginBottom: 37
  },
  attemptsError: {
    color: Colors.errorRedColor,
  },
  locked: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 23,
    color: Colors.errorRedColor,
    marginBottom: 37
  },
});
